import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
    getSubsessionClient,
    getVideoClient,
    onVideoSessionReady,
} from './useVideoSession';
import {
    ALLOCATION,
    MAX_SUBSESSIONS,
    humanizeSubsessionError,
    resolveSupported as resolveSupportedFlag,
    toOpenPayload,
} from './subsessionHelpers';

export { ALLOCATION, MAX_SUBSESSIONS };

/** SubsessionStatus del SDK. */
export const SUBSESSION_STATUS = {
    NotStarted: 1,
    InProgress: 2,
    Closing: 3,
    Closed: 4,
};

const USER_STATUS = {
    Initial: 'initial',
    Invited: 'invited',
    Joining: 'joining',
    InSubsession: 'in room',
    Leaving: 'leaving',
    MainSession: 'main session',
};

const HELP_RESPONSE_MESSAGE = {
    0: 'El profesor va en camino a tu grupo',
    1: 'El profesor está ayudando a otro grupo, espera un momento',
    2: 'El profesor pospuso tu solicitud',
    3: 'El profesor ya está en tu grupo',
};

const isDev = () => process.env.NODE_ENV !== 'production';

const logSubsession = (action, data = {}) => {
    if (!isDev()) return;
    console.log(`[Subsessions] ${action}`, {
        status: store.state.status,
        ...data,
    });
};

const logSubsessionError = (action, err, context = {}) => {
    if (!isDev()) return;
    console.error(`[Subsessions] ${action} FAILED`, {
        reason: err?.reason,
        message: err?.message,
        errorCode: err?.errorCode,
        ...context,
    });
};

const normalizeUserId = (userId) => {
    const n = Number(userId);
    if (!Number.isFinite(n)) {
        throw new Error(`ID de participante inválido: ${userId}`);
    }
    return n;
};

const isPreOpenStatus = (status) => (
    status === SUBSESSION_STATUS.NotStarted || status === SUBSESSION_STATUS.Closed
);

/** Ejecuta una promesa del SDK y valida ExecutedFailure. */
const executeSdk = async (promise, action, context = {}) => {
    try {
        const result = await promise;
        if (result && typeof result === 'object' && (result.reason || result.errorCode)) {
            logSubsessionError(action, result, context);
            throw result;
        }
        logSubsession(`${action}-ok`, context);
        return result;
    } catch (err) {
        logSubsessionError(action, err, context);
        throw err;
    }
};

/**
 * Antes de abrir grupos: mutar userList localmente (Zoom no permite
 * assignUserToSubsession hasta que openSubsessions haya corrido).
 */
const assignUserBeforeOpen = (sc, userId, targetSubsessionId) => {
    const list = sc.getSubsessionList() || [];
    logSubsession('assignUserBeforeOpen', {
        userId,
        targetSubsessionId,
        subsessionStatus: sc.getSubsessionStatus(),
        rooms: list.map((r) => ({
            id: r.subsessionId,
            name: r.subsessionName,
            users: (r.userList || []).length,
        })),
    });

    list.forEach((room) => {
        if (!room.userList) room.userList = [];
        room.userList = room.userList.filter((u) => Number(u.userId) !== userId);
    });

    const target = list.find((r) => r.subsessionId === targetSubsessionId);
    if (!target) {
        throw new Error(`Grupo no encontrado (${targetSubsessionId})`);
    }
    if (!target.userList) target.userList = [];

    const alreadyThere = target.userList.some((u) => Number(u.userId) === userId);
    if (alreadyThere) return;

    // Zoom sample: solo { userId }. displayName se resuelve en refresh/UI.
    target.userList.push({ userId });
};

/** Quita al participante de todos los grupos antes de abrirlos. */
const unassignUserBeforeOpen = (sc, userId) => {
    const list = sc.getSubsessionList() || [];
    logSubsession('unassignUserBeforeOpen', { userId });
    list.forEach((room) => {
        if (!room.userList) return;
        room.userList = room.userList.filter((u) => Number(u.userId) !== userId);
    });
};

/** Tras abrir grupos: assign/move dinámico del SDK. */
const assignUserAfterOpen = async (sc, userId, targetSubsessionId) => {
    const list = sc.getSubsessionList() || [];
    const currentRoom = list.find(
        (s) => (s.userList || []).some((u) => Number(u.userId) === userId),
    );
    const isUnassigned = (sc.getUnassignedUserList() || []).some(
        (u) => Number(u.userId) === userId,
    );

    logSubsession('assignUserAfterOpen', {
        userId,
        targetSubsessionId,
        currentRoomId: currentRoom?.subsessionId || null,
        isUnassigned,
        unassignedCount: (sc.getUnassignedUserList() || []).length,
    });

    if (currentRoom?.subsessionId === targetSubsessionId) return;

    if (currentRoom) {
        await executeSdk(
            sc.moveUserToSubsession(userId, targetSubsessionId),
            'moveUserToSubsession',
            { userId, targetSubsessionId, from: currentRoom.subsessionId },
        );
        return;
    }

    await executeSdk(
        sc.assignUserToSubsession(userId, targetSubsessionId),
        'assignUserToSubsession',
        { userId, targetSubsessionId, isUnassigned },
    );
};

/**
 * Opciones al abrir grupos: los alumnos entran solos al grupo pero no pueden
 * regresar ni elegir sala; solo el host decide cuándo salen.
 */
const OPEN_OPTIONS = {
    isAutoJoinSubsession: true,
    isBackToMainSessionEnabled: false,
    isSubsessionSelectionEnabled: false,
    isTimerEnabled: false,
    isAutoMoveBackToMainSession: true,
    waitSeconds: 10,
};

const initialState = () => ({
    ready: false,
    supported: true,
    /** Se confirma tras create/open exitoso; evita falsos negativos de isSubsessionEnabled(). */
    featureConfirmed: false,
    /**
     * Tras cerrar grupos, Zoom conserva las salas. Forzamos la UI de creación
     * hasta que el host vuelva a crear/configurar.
     */
    forceCreateUi: false,
    /** IDs del último create; evita reutilizar salas viejas al reabrir. */
    activeRoomIds: null,
    /**
     * Copia estable de salas mientras los grupos están abiertos.
     * Al entrar/salir de un subgrupo el SDK a veces devuelve lista parcial/vacía.
     */
    lastManagedRooms: [],
    /** Se incrementa al cerrar para que el panel reinicie estado local. */
    setupGeneration: 0,
    /** Acción en curso (join/leave/open/close…) para skeletons. */
    actionPending: false,
    isHost: false,
    status: SUBSESSION_STATUS.NotStarted,
    subsessions: [],
    unassigned: [],
    userStatus: USER_STATUS.MainSession,
    currentSubsessionId: null,
    currentSubsessionName: null,
    helpRequests: [],
    countdown: null,
    closingCountdown: null,
});

const resolveSupported = (sc, roomCount) => resolveSupportedFlag({
    roomCount,
    featureConfirmed: store.state.featureConfirmed,
    // Default true: isSubsessionEnabled() puede dar falsos negativos mid-session.
    sdkEnabled: safe(() => sc.isSubsessionEnabled(), true),
});

const store = {
    state: initialState(),
    listeners: new Set(),
    attachedClient: null,
};

const emit = () => {
    const snapshot = { ...store.state };
    store.listeners.forEach((cb) => {
        try {
            cb(snapshot);
        } catch (err) {
            console.warn('[Subsessions] listener:', err?.message || err);
        }
    });
};

const patch = (changes) => {
    store.state = { ...store.state, ...changes };
    emit();
};

const safe = (fn, fallback) => {
    try {
        const value = fn();
        return value === undefined ? fallback : value;
    } catch {
        return fallback;
    }
};

const mapMember = (u) => ({
    userId: u.userId,
    displayName: u.displayName || u.userName || 'Participante',
    isInSubsession: Boolean(u.isInSubsession),
});

const mapParticipant = (u) => ({
    userId: u.userId,
    displayName: u.displayName || u.userName || 'Participante',
    isInSubsession: false,
});

/** Antes de abrir, el SDK no siempre actualiza getUnassignedUserList tras mutar userList. */
const computePreOpenUnassigned = (sc, myUserId) => {
    const client = getVideoClient();
    const allUsers = client?.getAllUser?.() || [];
    const assignedIds = new Set(
        (sc.getSubsessionList() || [])
            .flatMap((r) => (r.userList || []).map((u) => Number(u.userId))),
    );

    return allUsers
        .filter((u) => !u.isHost && !u.isManager && Number(u.userId) !== Number(myUserId))
        .filter((u) => !assignedIds.has(Number(u.userId)))
        .map(mapParticipant);
};

const mapSubsession = (s) => ({
    subsessionId: s.subsessionId,
    subsessionName: s.subsessionName,
    userList: (s.userList || []).map(mapMember),
});

const isSelfHost = () => safe(() => {
    const me = getVideoClient()?.getCurrentUserInfo?.();
    return Boolean(me?.isHost || me?.isManager);
}, false);

/** Quita asignaciones locales de todas las salas (pre-open / post-close). */
const clearAllRoomAssignments = (sc) => {
    const list = sc?.getSubsessionList?.() || [];
    list.forEach((room) => {
        if (room.userList?.length) room.userList = [];
    });
};

/** Tras Closed: limpia asignaciones y vuelve la UI al paso de creación. */
const resetUiAfterClose = (sc) => {
    clearAllRoomAssignments(sc);
    const alreadyReset = store.state.forceCreateUi && store.state.activeRoomIds == null;
    patch({
        forceCreateUi: true,
        activeRoomIds: null,
        lastManagedRooms: [],
        setupGeneration: alreadyReset
            ? store.state.setupGeneration
            : store.state.setupGeneration + 1,
        countdown: null,
        closingCountdown: null,
        helpRequests: [],
        currentSubsessionId: null,
        currentSubsessionName: null,
    });
};

const filterActiveRooms = (rooms) => {
    const active = store.state.activeRoomIds;
    if (!active?.length) return rooms;
    const allowed = new Set(active.map(String));
    return rooms.filter((r) => allowed.has(String(r.subsessionId)));
};

/** Fusiona lista parcial del SDK (host dentro de un subgrupo) con la caché completa. */
const mergeManagedRooms = (cached, incoming) => {
    if (!cached?.length) return incoming;
    if (!incoming?.length) return cached;
    const incomingById = new Map(incoming.map((r) => [String(r.subsessionId), r]));
    return cached.map((room) => {
        const next = incomingById.get(String(room.subsessionId));
        if (!next) return room;
        return {
            ...room,
            ...next,
            userList: next.userList?.length ? next.userList : room.userList,
        };
    });
};

const withActionPending = async (fn) => {
    patch({ actionPending: true });
    try {
        return await fn();
    } finally {
        patch({ actionPending: false });
    }
};

const refresh = () => {
    const sc = getSubsessionClient();
    if (!sc) {
        store.state = initialState();
        emit();
        return;
    }

    const current = safe(() => sc.getCurrentSubsession(), null);
    const myUserId = safe(() => getVideoClient()?.getCurrentUserInfo?.()?.userId, null);
    const status = safe(() => sc.getSubsessionStatus(), SUBSESSION_STATUS.NotStarted);
    const live = status === SUBSESSION_STATUS.InProgress
        || status === SUBSESSION_STATUS.Closing;

    // Nunca mostrar creación mientras los grupos están abiertos/cerrando.
    const forceCreateUi = live ? false : store.state.forceCreateUi;

    const unassigned = isPreOpenStatus(status)
        ? computePreOpenUnassigned(sc, myUserId)
        : safe(() => (sc.getUnassignedUserList() || [])
            .filter((u) => u.userId !== myUserId)
            .map(mapMember), []);

    const allRooms = safe(() => (sc.getSubsessionList() || []).map(mapSubsession), []);
    let rooms = forceCreateUi ? [] : filterActiveRooms(allRooms);

    if (live) {
        const cached = store.state.lastManagedRooms || [];
        if (rooms.length === 0 && cached.length) {
            rooms = cached;
        } else if (cached.length > rooms.length) {
            rooms = mergeManagedRooms(cached, rooms);
        }
    }

    const lastManagedRooms = live && rooms.length
        ? rooms
        : (live ? store.state.lastManagedRooms : []);

    patch({
        ready: true,
        supported: resolveSupported(sc, allRooms.length || lastManagedRooms.length),
        featureConfirmed: store.state.featureConfirmed
            || allRooms.length > 0
            || lastManagedRooms.length > 0,
        forceCreateUi,
        lastManagedRooms,
        isHost: isSelfHost(),
        status,
        subsessions: rooms,
        unassigned,
        userStatus: current?.userStatus
            || safe(() => sc.getUserStatus(), USER_STATUS.MainSession),
        currentSubsessionId: forceCreateUi
            ? null
            : (current?.subsessionId || null),
        currentSubsessionName: forceCreateUi
            ? null
            : (current?.subsessionName || null),
    });
};

const onStateChange = (payload) => {
    const status = payload?.status;
    const prevStatus = store.state.status;
    const wasOpen = prevStatus === SUBSESSION_STATUS.InProgress;
    const wasClosing = prevStatus === SUBSESSION_STATUS.Closing;
    const host = store.state.isHost;
    const sc = getSubsessionClient();

    if (status === SUBSESSION_STATUS.InProgress && !wasOpen && !host) {
        toast.success('Los grupos de trabajo están abiertos');
    }
    if (status === SUBSESSION_STATUS.Closing && !host) {
        toast('El profesor está cerrando los grupos', { icon: '⏳' });
    }
    // Solo reiniciar UI tras un cierre real (InProgress/Closing → Closed).
    // No usar activeRoomIds: join/leave puede reportar estados raros sin cerrar.
    if (status === SUBSESSION_STATUS.Closed && (wasOpen || wasClosing)) {
        resetUiAfterClose(sc);
        if (!host) toast('Regresaste a la sala principal', { icon: '↩️' });
    }
    refresh();
};

const onInviteToJoin = async (payload) => {
    const name = payload?.subsessionName || 'un grupo';
    toast.success(`Te asignaron a ${name}`);
    // Los alumnos no eligen sala: si el auto-join no entró, entramos por ellos.
    if (!store.state.isHost && payload?.subsessionId) {
        const sc = getSubsessionClient();
        try {
            await sc?.joinSubsession(payload.subsessionId);
        } catch (err) {
            console.warn('[Subsessions] auto joinSubsession:', err?.reason || err?.message || err);
        }
    }
    refresh();
};

const onInviteBackToMain = async () => {
    const sc = getSubsessionClient();
    try {
        await sc?.leaveSubsession();
    } catch (err) {
        console.warn('[Subsessions] auto leaveSubsession:', err?.reason || err?.message || err);
    }
    refresh();
};

const onUserUpdate = () => refresh();

const onCountdown = (payload) => patch({ countdown: payload?.countdown ?? null });

const onTimeUp = () => {
    patch({ countdown: null });
    toast('Se terminó el tiempo del grupo', { icon: '⌛' });
};

const onClosingCountdown = (payload) => patch({
    closingCountdown: payload?.countdown ?? null,
});

const onBroadcastMessage = (payload) => {
    if (payload?.message) toast(payload.message, { icon: '📢', duration: 6000 });
};

const onAskForHelp = (payload) => {
    if (!payload?.userId) return;
    const request = {
        userId: payload.userId,
        displayName: payload.displayName || 'Participante',
        subsessionId: payload.subsessionId,
        subsessionName: payload.subsessionName,
        at: Date.now(),
    };
    const rest = store.state.helpRequests.filter((r) => r.userId !== request.userId);
    patch({ helpRequests: [...rest, request] });
    toast(`${request.displayName} pide ayuda en ${request.subsessionName || 'su grupo'}`, {
        icon: '🙋',
        duration: 6000,
    });
};

const onAskForHelpResponse = (payload) => {
    const message = HELP_RESPONSE_MESSAGE[payload?.result];
    if (message) toast(message, { icon: 'ℹ️' });
};

const CLIENT_EVENTS = [
    ['subsession-state-change', onStateChange],
    ['subsession-invite-to-join', onInviteToJoin],
    ['subsession-invite-back-to-main-session', onInviteBackToMain],
    ['subsession-user-update', onUserUpdate],
    ['subsession-countdown', onCountdown],
    ['subsession-time-up', onTimeUp],
    ['closing-subsession-countdown', onClosingCountdown],
    ['subsession-broadcast-message', onBroadcastMessage],
    ['subsession-ask-for-help', onAskForHelp],
    ['subsession-ask-for-help-response', onAskForHelpResponse],
    ['main-session-user-updated', onUserUpdate],
    ['user-added', onUserUpdate],
    ['user-removed', onUserUpdate],
    ['user-updated', onUserUpdate],
];

const detach = (client) => {
    if (!client) return;
    CLIENT_EVENTS.forEach(([event, handler]) => {
        try {
            client.off(event, handler);
        } catch {
            // el SDK ignora eventos no soportados
        }
    });
};

const attach = (client) => {
    if (!client) return;
    CLIENT_EVENTS.forEach(([event, handler]) => {
        try {
            client.on(event, handler);
        } catch (err) {
            console.warn(`[Subsessions] on(${event}):`, err?.message || err);
        }
    });
};

/** Engancha/desengancha los listeners según el ciclo de vida de la sesión. */
const bindClient = (client) => {
    if (store.attachedClient === client) {
        if (client) refresh();
        return;
    }
    detach(store.attachedClient);
    store.attachedClient = client || null;
    if (client) {
        attach(client);
        refresh();
    } else {
        store.state = initialState();
        emit();
    }
};

let unsubscribeReady = null;
let subscriberCount = 0;

const startBinding = () => {
    subscriberCount += 1;
    if (!unsubscribeReady) {
        unsubscribeReady = onVideoSessionReady(bindClient);
    }
};

const stopBinding = () => {
    subscriberCount -= 1;
    if (subscriberCount > 0) return;
    subscriberCount = 0;
    unsubscribeReady?.();
    unsubscribeReady = null;
    detach(store.attachedClient);
    store.attachedClient = null;
    store.state = initialState();
};

/** Suscripción al estado de subgrupos (para componentes fuera de la videollamada). */
export const onSubsessionStateChange = (callback) => {
    store.listeners.add(callback);
    startBinding();
    callback({ ...store.state });
    return () => {
        store.listeners.delete(callback);
        stopBinding();
    };
};

const requireClient = () => {
    const sc = getSubsessionClient();
    if (!sc) throw new Error('La sesión de video aún no está lista');
    return sc;
};

const failure = (err, fallback, context = {}) => {
    const hasRooms = context.hasRooms
        ?? (store.state.subsessions?.length > 0)
        ?? false;
    const reason = humanizeSubsessionError(err, fallback, { hasRooms });
    logSubsessionError(fallback, err);
    throw new Error(reason || fallback);
};

/** Estado de subgrupos de solo lectura. */
export const useSubsessionState = () => {
    const [state, setState] = useState(store.state);
    useEffect(() => onSubsessionStateChange(setState), []);
    return state;
};

/** Estado + acciones de subgrupos. */
export const useSubsessions = () => {
    const state = useSubsessionState();

    const createSubsessions = useCallback(async (count, pattern = ALLOCATION.MANUAL) => {
        const total = Math.max(1, Math.min(MAX_SUBSESSIONS, Number(count) || 0));
        return withActionPending(async () => {
            try {
                const sc = requireClient();
                const names = Array.from({ length: total }, (_, i) => `Sala ${i + 1}`);
                clearAllRoomAssignments(sc);

                logSubsession('createSubsessions-start', {
                    total,
                    pattern,
                    names,
                    forceCreateUi: store.state.forceCreateUi,
                    existing: (sc.getSubsessionList() || []).length,
                });

                let result = await sc.createSubsessions(names, pattern);

                // Tras un cierre, create a veces falla porque Zoom retiene salas: reutilizamos limpio.
                if (!Array.isArray(result) && store.state.forceCreateUi) {
                    const existing = sc.getSubsessionList() || [];
                    if (existing.length > 0) {
                        clearAllRoomAssignments(sc);
                        result = existing.slice(0, total).map((r) => ({
                            subsessionId: r.subsessionId,
                            subsessionName: r.subsessionName,
                            userList: [],
                        }));
                        logSubsession('createSubsessions-reuse-after-close', {
                            count: result.length,
                            ids: result.map((r) => r.subsessionId),
                        });
                    }
                }

                if (!Array.isArray(result)) {
                    logSubsessionError('createSubsessions', result, { total, pattern });
                    throw result instanceof Error ? result : new Error('No se pudieron crear los grupos');
                }

                const activeRoomIds = result.map((r) => r.subsessionId).filter(Boolean);
                logSubsession('createSubsessions-created', {
                    count: result.length,
                    ids: activeRoomIds,
                });
                patch({
                    featureConfirmed: true,
                    supported: true,
                    forceCreateUi: false,
                    activeRoomIds,
                    lastManagedRooms: [],
                });
                refresh();
                return activeRoomIds.length || total;
            } catch (err) {
                return failure(err, 'No se pudieron crear los grupos', { hasRooms: false });
            }
        });
    }, []);

    const openSubsessions = useCallback(async () => withActionPending(async () => {
        try {
            const sc = requireClient();
            const rawList = sc.getSubsessionList() || [];
            const active = store.state.activeRoomIds;
            const scoped = active?.length
                ? rawList.filter((r) => active.map(String).includes(String(r.subsessionId)))
                : rawList;
            if (!scoped.length) throw new Error('Primero crea los grupos');
            const list = toOpenPayload(scoped);
            logSubsession('openSubsessions-start', {
                rooms: list.map((r) => ({
                    id: r.subsessionId,
                    name: r.subsessionName,
                    assigned: (r.userList || []).length,
                })),
                options: OPEN_OPTIONS,
            });
            await executeSdk(
                sc.openSubsessions(list, OPEN_OPTIONS),
                'openSubsessions',
                { roomCount: list.length },
            );
            patch({ featureConfirmed: true, supported: true, forceCreateUi: false });
            refresh();
        } catch (err) {
            failure(err, 'No se pudieron abrir los grupos', { hasRooms: true });
        }
    }), []);

    const closeAllSubsessions = useCallback(async () => withActionPending(async () => {
        try {
            const sc = requireClient();
            await sc.closeAllSubsessions();
            patch({ helpRequests: [] });
            // Si el SDK ya quedó en Closed de inmediato, reiniciamos UI aquí.
            // Si pasa por Closing, onStateChange lo hará al llegar a Closed.
            const status = safe(() => sc.getSubsessionStatus(), null);
            if (status === SUBSESSION_STATUS.Closed) {
                resetUiAfterClose(sc);
            }
            refresh();
        } catch (err) {
            failure(err, 'No se pudieron cerrar los grupos');
        }
    }), []);

    const assignUser = useCallback(async (userIdRaw, subsessionId) => withActionPending(async () => {
        try {
            const sc = requireClient();
            const userId = normalizeUserId(userIdRaw);
            const status = sc.getSubsessionStatus();

            if (isPreOpenStatus(status)) {
                assignUserBeforeOpen(sc, userId, subsessionId);
            } else if (status === SUBSESSION_STATUS.InProgress) {
                await assignUserAfterOpen(sc, userId, subsessionId);
            } else {
                throw new Error('No se pueden asignar participantes mientras los grupos se están cerrando');
            }
            refresh();
        } catch (err) {
            failure(err, 'No se pudo asignar al participante');
        }
    }), []);

    const moveUserToMainSession = useCallback(async (userIdRaw) => withActionPending(async () => {
        try {
            const sc = requireClient();
            const userId = normalizeUserId(userIdRaw);
            const status = sc.getSubsessionStatus();

            if (isPreOpenStatus(status)) {
                unassignUserBeforeOpen(sc, userId);
            } else if (status === SUBSESSION_STATUS.InProgress) {
                await executeSdk(
                    sc.moveBackToMainSession(userId),
                    'moveBackToMainSession',
                    { userId },
                );
            } else {
                throw new Error('No se puede mover participantes mientras los grupos se están cerrando');
            }
            refresh();
        } catch (err) {
            failure(err, 'No se pudo regresar al participante');
        }
    }), []);

    const joinSubsession = useCallback(async (subsessionId) => withActionPending(async () => {
        try {
            const sc = requireClient();
            if (store.state.currentSubsessionId
                && store.state.currentSubsessionId !== subsessionId) {
                await sc.leaveSubsession();
            }
            await sc.joinSubsession(subsessionId);
            refresh();
        } catch (err) {
            failure(err, 'No se pudo entrar al grupo');
        }
    }), []);

    const leaveSubsession = useCallback(async () => withActionPending(async () => {
        try {
            const sc = requireClient();
            await sc.leaveSubsession();
            refresh();
        } catch (err) {
            failure(err, 'No se pudo salir del grupo');
        }
    }), []);

    const askForHelp = useCallback(async () => {
        try {
            const sc = requireClient();
            await sc.askForHelp();
        } catch (err) {
            failure(err, 'No se pudo pedir ayuda');
        }
    }, []);

    const postponeHelping = useCallback(async (userId) => {
        try {
            const sc = requireClient();
            await sc.postponeHelping(userId);
        } catch (err) {
            console.warn('[Subsessions] postponeHelping:', err?.reason || err?.message || err);
        } finally {
            patch({
                helpRequests: store.state.helpRequests.filter((r) => r.userId !== userId),
            });
        }
    }, []);

    const dismissHelpRequest = useCallback((userId) => {
        patch({
            helpRequests: store.state.helpRequests.filter((r) => r.userId !== userId),
        });
    }, []);

    const broadcast = useCallback(async (message) => {
        const text = String(message || '').trim();
        if (!text) return;
        try {
            const sc = requireClient();
            await sc.broadcast(text);
        } catch (err) {
            failure(err, 'No se pudo enviar el mensaje');
        }
    }, []);

    return {
        ...state,
        isOpen: state.status === SUBSESSION_STATUS.InProgress,
        isClosing: state.status === SUBSESSION_STATUS.Closing,
        inSubsession: state.userStatus === USER_STATUS.InSubsession,
        createSubsessions,
        openSubsessions,
        closeAllSubsessions,
        assignUser,
        moveUserToMainSession,
        joinSubsession,
        leaveSubsession,
        askForHelp,
        postponeHelping,
        dismissHelpRequest,
        broadcast,
        refresh,
    };
};
